import { useEffect, useState } from "react";
import ArtistFormFields from "../../artists/ArtistFormFields";
import Button from "../../../components/Button";
import { useArtistDetail } from "../../../../hooks/useArtistDetail";
import { useUpdateArtist } from "../../../../hooks/useUpdateArtist";
import type { ArtistDto } from "../../../../api/artistsApi";

interface Props {
  artistId: string;
  onClose: () => void;
}

/**
 * Compact edit form embedded in ParcoursArtistsTab so the editor can tweak an artist
 * without leaving the parcours page. Fetches the full artist on mount so lieu links
 * are reflected in the form fields.
 */
export default function InlineArtistEditor({ artistId, onClose }: Props) {
  const detail = useArtistDetail(artistId);
  const update = useUpdateArtist(artistId);
  const [draft, setDraft] = useState<ArtistDto | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    const a = detail.data;
    setDraft({
      name: a.name,
      description: a.description ?? "",
      photoLink: a.photoLink ?? "",
      lieuIds: (a.lieux ?? []).map((l) => l.id),
    });
  }, [detail.data]);

  if (detail.isLoading || !draft) {
    return <div className="bo-loading" style={{ padding: "var(--sp-3)" }}>Chargement…</div>;
  }
  if (detail.isError) {
    return <p className="bo-error-text">Impossible de charger cet artiste.</p>;
  }

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(draft, { onSuccess: () => onClose() });
  };

  return (
    <form onSubmit={onSave} style={{ paddingTop: "var(--sp-4)", borderTop: "1px solid var(--line-soft)" }}>
      <ArtistFormFields value={draft} onChange={setDraft} attachedLieux={detail.data.lieux ?? []} />
      <div style={{ marginTop: "var(--sp-4)", display: "flex", gap: "var(--sp-2)", justifyContent: "flex-end" }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" variant="primary" loading={update.isPending}>
          Enregistrer
        </Button>
      </div>
      {update.isError && (
        <p className="bo-error-text" style={{ marginTop: "var(--sp-2)" }}>
          Erreur : {(update.error as Error)?.message}
        </p>
      )}
    </form>
  );
}
